import { createElement } from 'react';
import Skeleton from 'Common/Components/Skeleton';
import resolveValue from './resolveValue';
import normalizeColumns from './normalizeColumns';
import { RESOLVABLE_COLUMN_KEYS, RENDER_ARITY, HIDE_SORT_ICON_CLASS } from '../Table.constants';

const byCustomOrder = (a, b) => a.custom_order - b.custom_order;

const withContext = (renderFn, ctx, arity) => (...args) => {
  const engineArgs = Array.from({ length: arity }, (_, index) => args[index]);
  return renderFn(...engineArgs, ctx);
};

const resolveKeys = (column, ctx) => RESOLVABLE_COLUMN_KEYS.reduce((acc, key) => {
  if (key in column) acc[key] = resolveValue(column[key], ctx);
  return acc;
}, {});

const joinClassNames = (...classNames) => classNames.filter(Boolean).join(' ');

const renderSkeleton = () => createElement(Skeleton);

const toCell = (cell, ctx, showSkeleton) => (showSkeleton ? renderSkeleton : withContext(cell, ctx, RENDER_ARITY.Cell));

/**
 * Translates one schema column into an engine (react-table-6) column.
 * Neutral keys map to the engine ones:
 * - `header`/`cell`: render functions `(info, ctx) => node` → `Header`/`Cell`;
 * - `visible`/`order` → `show`/`custom_order`;
 * - `cellProps(rowInfo, ctx)` → `getProps`;
 * - `showSortIcon: false` → the `hideSortIcon` header class;
 * - `expander(info, ctx)` → an expander column (`expander: true` + `Expander`);
 * - `skeleton: false` opts the column out of the table skeleton.
 * Any other key passes through untouched.
 *
 * @param {Object} column - schema column
 * @param {Object} ctx - table context
 * @param {Object} [options]
 * @param {boolean} [options.showSkeleton=false] - render a Skeleton instead of the cell
 * @return {Object} engine column
 */
export const toEngineColumn = (column, ctx, { showSkeleton = false } = {}) => {
  const { header, cell, visible, order, cellProps, showSortIcon, expander, skeleton, ...rest } = column;
  const { visible: show, order: customOrder, ...resolved } = resolveKeys(column, ctx);
  const engineColumn = { ...rest, ...resolved };

  if (header !== undefined) engineColumn.Header = typeof header === 'function' ? withContext(header, ctx, RENDER_ARITY.Header) : header;
  if (cell !== undefined) engineColumn.Cell = toCell(cell, ctx, showSkeleton && skeleton !== false);
  if ('visible' in column) engineColumn.show = show;
  if ('order' in column) engineColumn.custom_order = customOrder;
  if (cellProps) engineColumn.getProps = (state, rowInfo) => cellProps(rowInfo, ctx);
  if (showSortIcon === false) engineColumn.headerClassName = joinClassNames(engineColumn.headerClassName, HIDE_SORT_ICON_CLASS);
  if (typeof expander === 'function') {
    engineColumn.expander = true;
    engineColumn.Expander = withContext(expander, ctx, RENDER_ARITY.Expander);
  } else if (expander !== undefined) {
    engineColumn.expander = expander;
  }

  return engineColumn;
};

/**
 * Drops hidden columns and sorts by `custom_order`, exactly like the tables with column
 * preferences do today. The first column is kept even when hidden (expander/selection).
 *
 * @param {Object[]} columns - engine columns
 * @param {Object} ordering - `{ keepFirstColumn = true, compare = byCustomOrder, normalizeOrder = true }`;
 * `normalizeOrder: false` sorts on the raw `custom_order` (undefined stays undefined)
 * @return {Object[]} visible columns in display order
 */
export const applyOrdering = (columns, { keepFirstColumn = true, compare = byCustomOrder, normalizeOrder = true } = {}) => columns
  .map((column) => (normalizeOrder
    ? { ...column, custom_order: typeof column.custom_order === 'undefined' ? 0 : column.custom_order }
    : column))
  .filter((column, index) => column.show === true || (keepFirstColumn && index === 0))
  .sort(compare);

/**
 * Builds the engine columns of a table schema for the given context.
 *
 * @param {Object} schema - table schema (`columns` may be an array or `(ctx) => array`;
 * `loading.skeleton(ctx)` renders a Skeleton in every cell while it returns true)
 * @param {Object} ctx - table context provided by the view
 * @return {Object[]} engine columns
 */
const buildColumns = (schema, ctx = {}) => {
  const schemaColumns = resolveValue(schema.columns, ctx) || [];
  const showSkeleton = Boolean(resolveValue(schema.loading?.skeleton, ctx));
  const engineColumns = schemaColumns.map((column) => toEngineColumn(column, ctx, { showSkeleton }));
  const orderedColumns = schema.ordering ? applyOrdering(engineColumns, schema.ordering) : engineColumns;

  return schema.normalizeWidths ? normalizeColumns(orderedColumns) : orderedColumns;
};

export default buildColumns;
